import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as Icon from 'react-feather';
import './FloatingAlerts.css';

const ALERT_DURATION = 6500;

const VARIANT_META = {
    success: {
        title: 'Listo',
        IconComponent: Icon.CheckCircle,
    },
    danger: {
        title: 'Error',
        IconComponent: Icon.AlertCircle,
    },
    warning: {
        title: 'Atencion',
        IconComponent: Icon.AlertTriangle,
    },
    info: {
        title: 'Aviso',
        IconComponent: Icon.Info,
    },
};

const getAlertMessage = (input) => {
    if (input instanceof Error) {
        return input.message || String(input);
    }

    if (typeof input === 'object' && input !== null) {
        return input.response?.data?.error
            || input.response?.data?.msg
            || input.message
            || String(input);
    }

    return String(input);
};

const getAlertVariant = (input, message) => {
    if (input instanceof Error || (typeof input === 'object' && input !== null)) {
        return 'danger';
    }

    const normalizedMessage = message.toLowerCase();

    if (
        normalizedMessage.includes('exitosamente')
        || normalizedMessage.includes('agregada exitosamente')
        || normalizedMessage.includes('actualizada exitosamente')
        || normalizedMessage.includes('creada exitosamente')
    ) {
        return 'success';
    }

    if (
        normalizedMessage.includes('por favor')
        || normalizedMessage.includes('selecciona')
        || normalizedMessage.includes('capture')
        || normalizedMessage.includes('requerido')
    ) {
        return 'warning';
    }

    if (
        normalizedMessage.includes('error')
        || normalizedMessage.includes('no se pudo')
        || normalizedMessage.includes('network')
        || normalizedMessage.includes('axios')
    ) {
        return 'danger';
    }

    return 'info';
};

const FloatingAlerts = () => {
    const [alerts, setAlerts] = useState([]);
    const timersRef = useRef({});

    const removeAlert = useCallback((id) => {
        window.clearTimeout(timersRef.current[id]);
        delete timersRef.current[id];

        setAlerts((currentAlerts) => currentAlerts.filter((alertItem) => alertItem.id !== id));
    }, []);

    const addAlert = useCallback((input) => {
        const message = getAlertMessage(input);
        const variant = getAlertVariant(input, message);
        const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

        setAlerts((currentAlerts) => {
            const nextAlerts = [
                { id, message, variant },
                ...currentAlerts,
            ].slice(0, 4);
            const nextAlertIds = nextAlerts.map((alertItem) => alertItem.id);

            currentAlerts.forEach((alertItem) => {
                if (!nextAlertIds.includes(alertItem.id)) {
                    window.clearTimeout(timersRef.current[alertItem.id]);
                    delete timersRef.current[alertItem.id];
                }
            });

            return nextAlerts;
        });

        timersRef.current[id] = window.setTimeout(() => {
            removeAlert(id);
        }, ALERT_DURATION);
    }, [removeAlert]);

    useEffect(() => {
        const nativeAlert = window.alert;

        window.alert = (input) => {
            addAlert(input);
        };

        return () => {
            window.alert = nativeAlert;

            Object.values(timersRef.current).forEach((timer) => {
                window.clearTimeout(timer);
            });
            timersRef.current = {};
        };
    }, [addAlert]);

    if (alerts.length === 0) {
        return null;
    }

    return (
        <div className="floating-alerts" aria-live="polite" aria-relevant="additions">
            {alerts.map((alertItem) => {
                const meta = VARIANT_META[alertItem.variant];
                const AlertIcon = meta.IconComponent;

                return (
                    <div
                        key={alertItem.id}
                        className={`floating-alert floating-alert--${alertItem.variant}`}
                        role="alert"
                    >
                        <AlertIcon className="floating-alert__icon" />
                        <div>
                            <p className="floating-alert__title">{meta.title}</p>
                            <p className="floating-alert__message">{alertItem.message}</p>
                        </div>
                        <button
                            type="button"
                            className="floating-alert__close"
                            aria-label="Cerrar alerta"
                            onClick={() => removeAlert(alertItem.id)}
                        >
                            <Icon.X className="icon" />
                        </button>
                    </div>
                );
            })}
        </div>
    );
};

export default FloatingAlerts;
